import { useState } from "react";
import { useToast } from "@/components/feature/Toast";

const SUBMIT_URL = "https://readdy.ai/api/form/dbaead5q007e26o3bke0";

const topics = ["内容纠错", "合作洽谈", "意见建议", "版权反馈"];

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [topic, setTopic] = useState(topics[0]);
  const [message, setMessage] = useState("");
  const [formKey, setFormKey] = useState(0);
  const { showToast } = useToast();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      showToast("留言已提交，感谢你的反馈", "success");
      return;
    }
    formData.delete("company_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (!response.ok || isSpam || parsed?.code !== "OK") {
        setFormError(
          isSpam
            ? "提交内容被系统判定为异常，请修改留言内容后重试"
            : serverMsg || "提交失败，请稍后重试",
        );
        setStatus("error");
        return;
      }

      setStatus("success");
      setMessage("");
      setFormKey((k) => k + 1);
      showToast("留言已提交，感谢你的反馈", "success");
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "网络异常，请稍后重试");
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="animate-pop flex flex-col items-center justify-center rounded-lg border border-accent-200 bg-accent-50 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-background-50">
          <i className="ri-check-line text-xl" />
        </span>
        <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">留言已提交</h3>
        <p className="mt-2 max-w-sm text-[12px] leading-relaxed text-foreground-600">
          感谢你对 97视频 的反馈，我们会在 3 个工作日内查看。如需继续留言，可再次填写表单。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-5 cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-[12px] text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600"
        >
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      key={formKey}
      id="contact-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="contact-name" className="mb-1.5 block font-label text-[11px] text-foreground-600">
            你的称呼
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="例如：影迷小王"
            className="h-10 w-full rounded-md border border-background-300 bg-background-100/60 px-3 text-[13px] text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
          />
        </div>
        <div>
          <label htmlFor="contact-email" className="mb-1.5 block font-label text-[11px] text-foreground-600">
            联系邮箱
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            className="h-10 w-full rounded-md border border-background-300 bg-background-100/60 px-3 text-[13px] text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
          />
        </div>
      </div>

      <div className="mt-3">
        <span className="mb-1.5 block font-label text-[11px] text-foreground-600">反馈类型</span>
        <div className="flex flex-wrap gap-1.5">
          {topics.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTopic(item)}
              className={`cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-[12px] transition-colors ${
                topic === item
                  ? "bg-primary-500 font-medium text-background-50"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              {item}
            </button>
          ))}
        </div>
        <input type="hidden" name="topic" value={topic} />
      </div>

      <div className="mt-3">
        <label htmlFor="contact-message" className="mb-1.5 flex items-center justify-between font-label text-[11px] text-foreground-600">
          <span>留言内容</span>
          <span className={message.length > 500 ? "text-accent-700" : "text-foreground-400"}>
            {message.length} / 500
          </span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, 500))}
          placeholder="告诉我们你遇到的问题或建议，例如某部影视资料有误……"
          className="w-full resize-none rounded-md border border-background-300 bg-background-100/60 px-3 py-2.5 text-[13px] leading-relaxed text-foreground-900 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
        />
      </div>

      <div className="contact-form-hp" aria-hidden="true">
        <label htmlFor="contact-alt-field">公司名称</label>
        <input
          id="contact-alt-field"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />
      </div>

      {status === "error" && formError ? (
        <p className="mt-3 flex items-start gap-2 rounded-md bg-accent-50 px-3 py-2 text-[11px] leading-relaxed text-accent-800">
          <i className="ri-error-warning-line mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center" />
          {formError}
        </p>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-foreground-400">留言仅用于内容反馈，我们不会用于其他用途</p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-[13px] font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <i className="ri-loader-4-line flex h-4 w-4 animate-spin items-center justify-center" />
              提交中…
            </>
          ) : (
            <>
              <i className="ri-send-plane-line flex h-4 w-4 items-center justify-center" />
              提交留言
            </>
          )}
        </button>
      </div>
    </form>
  );
}